import React, { useState, useEffect, useRef } from 'react';
import { Paintbrush, Linkedin, Github, Twitter, Dribbble } from 'lucide-react';
import { SimpleIcons } from '../../../utils/simpleIcons';
import '../../../styles/resume-editor.css';

interface SocialLink {
  type: 'linkedin' | 'telegram' | 'whatsapp' | 'github' | 'twitter' | 'dribbble';
  handle: string;
}

interface CustomLink {
  uid: string;
  title: string;
  url: string;
}

interface LinkBlocks {
  uid?: string;
  display_format?: 'alias' | 'url';
  linkedin_handler?: string | null;
  telegram_handler?: string | null;
  whatsapp_handler?: string | null;
  github_handler?: string | null;
  twitter_handler?: string | null;
  dribbble_handler?: string | null;
  custom_links?: Array<{ name: string; url: string }> | null;
}

export type DisplayFormat = 'alias' | 'url';

interface SocialSectionProps {
  initialLinks?: SocialLink[];
  onLinksChange?: (links: SocialLink[]) => void;
  initialLinkBlocks?: LinkBlocks | null;
  onLinkBlocksChange?: (linkBlocks: LinkBlocks) => void;
  activeField?: string | null;
  onActiveFieldChange?: (field: string | null) => void;
  displayFormat?: DisplayFormat;
  onDisplayFormatChange?: (format: DisplayFormat) => void;
}

export const SocialSection: React.FC<SocialSectionProps> = ({
  initialLinks = [],
  onLinksChange,
  initialLinkBlocks,
  onLinkBlocksChange,
  activeField,
  onActiveFieldChange,
  displayFormat = 'alias',
  onDisplayFormatChange,
}) => {
  const [links, setLinks] = useState<SocialLink[]>(initialLinks);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [localDisplayFormat, setLocalDisplayFormat] = useState<DisplayFormat>(displayFormat);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isClickingInsideRef = useRef<boolean>(false);

  useEffect(() => {
    if (activeField && (activeField === 'linkedin' || activeField === 'telegram' || activeField === 'whatsapp' || activeField === 'github' || activeField === 'twitter' || activeField === 'dribbble')) {
      if (editingField !== activeField) {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        isClickingInsideRef.current = false;
        setEditingField(activeField);
        
        let handleValue = '';
        if (initialLinkBlocks) {
          switch (activeField) {
            case 'linkedin':
              handleValue = initialLinkBlocks.linkedin_handler || '';
              break;
            case 'telegram':
              handleValue = initialLinkBlocks.telegram_handler || '';
              break;
            case 'whatsapp':
              handleValue = initialLinkBlocks.whatsapp_handler || '';
              break;
            case 'github':
              handleValue = initialLinkBlocks.github_handler || '';
              break;
            case 'twitter':
              handleValue = initialLinkBlocks.twitter_handler || '';
              break;
            case 'dribbble':
              handleValue = initialLinkBlocks.dribbble_handler || '';
              break;
          }
        }
        
        if (!handleValue) {
          const link = links.find(l => l.type === activeField);
          handleValue = link?.handle || '';
        }
        
        setEditingValue(handleValue);
      }
    }
  }, [activeField, links, editingField, initialLinkBlocks]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);
  const [customLinks, setCustomLinks] = useState<CustomLink[]>([]);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  useEffect(() => {
    if (initialLinkBlocks) {
      const socialLinks: SocialLink[] = [];
      if (initialLinkBlocks.linkedin_handler) {
        socialLinks.push({ type: 'linkedin', handle: initialLinkBlocks.linkedin_handler });
      }
      if (initialLinkBlocks.telegram_handler) {
        socialLinks.push({ type: 'telegram', handle: initialLinkBlocks.telegram_handler });
      }
      if (initialLinkBlocks.whatsapp_handler) {
        socialLinks.push({ type: 'whatsapp', handle: initialLinkBlocks.whatsapp_handler });
      }
      if (initialLinkBlocks.github_handler) {
        socialLinks.push({ type: 'github', handle: initialLinkBlocks.github_handler });
      }
      if (initialLinkBlocks.twitter_handler) {
        socialLinks.push({ type: 'twitter', handle: initialLinkBlocks.twitter_handler });
      }
      if (initialLinkBlocks.dribbble_handler) {
        socialLinks.push({ type: 'dribbble', handle: initialLinkBlocks.dribbble_handler });
      }
      setLinks(socialLinks);

      if (initialLinkBlocks.custom_links) {
        setCustomLinks(
          initialLinkBlocks.custom_links.map((link, index) => ({
            uid: `link-${index}`,
            title: link.name,
            url: link.url,
          }))
        );
      } else {
        setCustomLinks([]);
      }
    }
  }, [initialLinkBlocks]);

  useEffect(() => {
    if (displayFormat) {
      setLocalDisplayFormat(displayFormat);
    }
  }, [displayFormat]);

  const handleDisplayFormatChange = (format: DisplayFormat) => {
    setLocalDisplayFormat(format);
    onDisplayFormatChange?.(format);
  };

  const socialNetworks = [
    { type: 'linkedin' as const, name: 'LinkedIn', baseUrl: 'linkedin.com/in/', icon: 'in' },
    { type: 'telegram' as const, name: 'Telegram', baseUrl: 't.me/', icon: null },
    { type: 'whatsapp' as const, name: 'WhatsApp', baseUrl: 'wa.me/', icon: null },
    { type: 'github' as const, name: 'GitHub', baseUrl: 'github.com/', icon: null },
    { type: 'twitter' as const, name: 'Twitter', baseUrl: 'twitter.com/', icon: null },
    { type: 'dribbble' as const, name: 'Dribbble', baseUrl: 'dribbble.com/', icon: null },
  ];

  const getLink = (type: string) => {
    return links.find(l => l.type === type);
  };

  const handleEdit = (type: string) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    onActiveFieldChange?.(type);
    
    let handleValue = '';
    if (initialLinkBlocks) {
      switch (type) {
        case 'linkedin':
          handleValue = initialLinkBlocks.linkedin_handler || '';
          break;
        case 'telegram':
          handleValue = initialLinkBlocks.telegram_handler || '';
          break;
        case 'whatsapp':
          handleValue = initialLinkBlocks.whatsapp_handler || '';
          break;
        case 'github':
          handleValue = initialLinkBlocks.github_handler || '';
          break;
        case 'twitter':
          handleValue = initialLinkBlocks.twitter_handler || '';
          break;
        case 'dribbble':
          handleValue = initialLinkBlocks.dribbble_handler || '';
          break;
      }
    }
    
    if (!handleValue) {
      const link = getLink(type);
      handleValue = link?.handle || '';
    }
    
    setEditingField(type);
    setEditingValue(handleValue);
  };

  const handleSave = (type: string) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    if (!editingValue.trim()) {
      const link = getLink(type);
      if (!link?.handle) {
        setTimeout(() => {
          setEditingField(null);
          setEditingValue('');
        }, 0);
        return;
      }
      setTimeout(() => {
        setEditingField(null);
        setEditingValue('');
      }, 0);
      return;
    }

    const newLinks = [...links];
    const existingIndex = newLinks.findIndex(l => l.type === type);
    
    if (existingIndex >= 0) {
      newLinks[existingIndex] = { type: type as SocialLink['type'], handle: editingValue.trim() };
    } else {
      newLinks.push({ type: type as SocialLink['type'], handle: editingValue.trim() });
    }
    
    setLinks(newLinks);
    onLinksChange?.(newLinks);
    
    if (onLinkBlocksChange) {
      const linkedinLink = newLinks.find(l => l.type === 'linkedin');
      const telegramLink = newLinks.find(l => l.type === 'telegram');
      const whatsappLink = newLinks.find(l => l.type === 'whatsapp');
      const githubLink = newLinks.find(l => l.type === 'github');
      const twitterLink = newLinks.find(l => l.type === 'twitter');
      const dribbbleLink = newLinks.find(l => l.type === 'dribbble');
      
      const linkBlocks: LinkBlocks = {
        uid: initialLinkBlocks?.uid,
        display_format: localDisplayFormat,
        linkedin_handler: linkedinLink?.handle ? linkedinLink.handle : null,
        telegram_handler: telegramLink?.handle ? telegramLink.handle : null,
        whatsapp_handler: whatsappLink?.handle ? whatsappLink.handle : null,
        github_handler: githubLink?.handle ? githubLink.handle : null,
        twitter_handler: twitterLink?.handle ? twitterLink.handle : null,
        dribbble_handler: dribbbleLink?.handle ? dribbbleLink.handle : null,
        custom_links: customLinks.length > 0 ? customLinks.map(link => ({ name: link.title, url: link.url })) : null,
      };
      onLinkBlocksChange(linkBlocks);
    }
    
    setTimeout(() => {
      setEditingField(null);
      setEditingValue('');
      onActiveFieldChange?.(null);
    }, 0);
  };

  const handleClear = (type: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const newLinks = [...links];
    const existingIndex = newLinks.findIndex(l => l.type === type);
    
    if (existingIndex >= 0) {
      newLinks.splice(existingIndex, 1);
    }
    
    setLinks(newLinks);
    onLinksChange?.(newLinks);
    
    if (onLinkBlocksChange) {
      const linkedinLink = newLinks.find(l => l.type === 'linkedin');
      const telegramLink = newLinks.find(l => l.type === 'telegram');
      const whatsappLink = newLinks.find(l => l.type === 'whatsapp');
      const githubLink = newLinks.find(l => l.type === 'github');
      const twitterLink = newLinks.find(l => l.type === 'twitter');
      const dribbbleLink = newLinks.find(l => l.type === 'dribbble');
      
      const linkBlocks: LinkBlocks = {
        uid: initialLinkBlocks?.uid,
        display_format: localDisplayFormat,
        linkedin_handler: linkedinLink?.handle ? linkedinLink.handle : null,
        telegram_handler: telegramLink?.handle ? telegramLink.handle : null,
        whatsapp_handler: whatsappLink?.handle ? whatsappLink.handle : null,
        github_handler: githubLink?.handle ? githubLink.handle : null,
        twitter_handler: twitterLink?.handle ? twitterLink.handle : null,
        dribbble_handler: dribbbleLink?.handle ? dribbbleLink.handle : null,
        custom_links: customLinks.length > 0 ? customLinks.map(link => ({ name: link.title, url: link.url })) : null,
      };
      onLinkBlocksChange(linkBlocks);
    }
    
    if (editingField === type) {
      setEditingField(null);
      setEditingValue('');
    }
  };

  const handleCancel = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingField(null);
    setEditingValue('');
    onActiveFieldChange?.(null);
  };

  const handleAddLink = () => {
    setIsAddingLink(true);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleSaveLink = () => {
    if (!newLinkTitle.trim() || !newLinkUrl.trim()) {
      return;
    }
    
    const newLink: CustomLink = {
      uid: `link-${Date.now()}`,
      title: newLinkTitle.trim(),
      url: newLinkUrl.trim(),
    };
    
    const updatedCustomLinks = [...customLinks, newLink];
    setCustomLinks(updatedCustomLinks);
    
    if (onLinkBlocksChange) {
      const linkBlocks: LinkBlocks = {
        uid: initialLinkBlocks?.uid,
        display_format: localDisplayFormat,
        linkedin_handler: links.find(l => l.type === 'linkedin')?.handle || null,
        telegram_handler: links.find(l => l.type === 'telegram')?.handle || null,
        whatsapp_handler: links.find(l => l.type === 'whatsapp')?.handle || null,
        github_handler: links.find(l => l.type === 'github')?.handle || null,
        twitter_handler: links.find(l => l.type === 'twitter')?.handle || null,
        dribbble_handler: links.find(l => l.type === 'dribbble')?.handle || null,
        custom_links: updatedCustomLinks.map(link => ({ name: link.title, url: link.url })) || null,
      };
      onLinkBlocksChange(linkBlocks);
    }
    
    setIsAddingLink(false);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleCancelLink = () => {
    setIsAddingLink(false);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'linkedin':
        return <Linkedin size={16} />;
      case 'github':
        return <Github size={16} />;
      case 'twitter':
        return <Twitter size={16} />;
      case 'dribbble':
        return <Dribbble size={16} />;
      case 'telegram':
        return <SimpleIcons.Telegram size={16} />;
      case 'whatsapp':
        return <SimpleIcons.WhatsApp size={16} />;
      default:
        return null;
    }
  };

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="resume-editor__social-items">
        {socialNetworks.map((network) => {
          const link = getLink(network.type);
          const isEditing = editingField === network.type;

          return (
            <div
              key={network.type}
              className="resume-editor__social-item"
              onMouseDown={(e) => {
                isClickingInsideRef.current = true;
                if (isEditing && editingField === network.type) {
                  e.preventDefault();
                  setTimeout(() => {
                    isClickingInsideRef.current = false;
                  }, 100);
                  return;
                }
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                if (!isEditing) {
                  handleEdit(network.type);
                }
                setTimeout(() => {
                  isClickingInsideRef.current = false;
                }, 100);
              }}
            >
              <div className={`resume-editor__social-icon resume-editor__social-icon--${network.type}`}>
                {getIcon(network.type)}
              </div>
              <div className="resume-editor__social-content">
                <div className="resume-editor__social-name">{network.name}</div>
                {isEditing ? (
                  <input
                    type="text"
                    className="resume-editor__social-input"
                    placeholder="username"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={(e) => {
                      const relatedTarget = e.relatedTarget as HTMLElement | null;
                      const socialItem = e.currentTarget.closest('.resume-editor__social-item');
                      
                      if (relatedTarget && socialItem?.contains(relatedTarget)) {
                        return;
                      }
                      
                      if (isClickingInsideRef.current) {
                        isClickingInsideRef.current = false;
                        return;
                      }
                      
                      blurTimeoutRef.current = setTimeout(() => {
                        if (!isClickingInsideRef.current) {
                          handleSave(network.type);
                        }
                        blurTimeoutRef.current = null;
                      }, 200);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (blurTimeoutRef.current) {
                          clearTimeout(blurTimeoutRef.current);
                          blurTimeoutRef.current = null;
                        }
                        handleSave(network.type);
                      } else if (e.key === 'Escape') {
                        if (blurTimeoutRef.current) {
                          clearTimeout(blurTimeoutRef.current);
                          blurTimeoutRef.current = null;
                        }
                        handleCancel();
                      }
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="resume-editor__social-url">
                    <span className="resume-editor__social-url-base">{network.baseUrl}</span>
                    <span className="resume-editor__social-url-handle">
                      {link?.handle || 'username'}
                    </span>
                  </div>
                )}
              </div>
              {!isEditing && link?.handle && (
                <button
                  className="resume-editor__social-clear"
                  onClick={(e) => {
                    if (blurTimeoutRef.current) {
                      clearTimeout(blurTimeoutRef.current);
                      blurTimeoutRef.current = null;
                    }
                    handleClear(network.type, e);
                  }}
                  onMouseDown={(e) => {
                    isClickingInsideRef.current = true;
                    e.preventDefault();
                    e.stopPropagation();
                    setTimeout(() => {
                      isClickingInsideRef.current = false;
                    }, 100);
                  }}
                  title="Clear alias"
                >
                  <Paintbrush size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="resume-editor__display-format-section">
        <div className="resume-editor__display-format-label">Display Format</div>
        <div className="resume-editor__display-format-options">
          <label className="resume-editor__display-format-option">
            <input
              type="radio"
              name="displayFormat"
              value="alias"
              checked={localDisplayFormat === 'alias'}
              onChange={() => handleDisplayFormatChange('alias')}
            />
            <span>Alias</span>
          </label>
          <label className="resume-editor__display-format-option">
            <input
              type="radio"
              name="displayFormat"
              value="url"
              checked={localDisplayFormat === 'url'}
              onChange={() => handleDisplayFormatChange('url')}
            />
            <span>URL</span>
          </label>
        </div>
      </div>

      <div className="resume-editor__links-section">
        <h3 className="resume-editor__links-title">Links</h3>
        <p className="resume-editor__links-description">
          Add links to your personal site or portfolio here.
        </p>
        
        {!isAddingLink ? (
          <button
            className="resume-editor__add-link-btn"
            onClick={handleAddLink}
          >
            + Add link
          </button>
        ) : (
          <div className="resume-editor__add-link-form">
            <div className="resume-editor__add-link-field">
              <label className="resume-editor__add-link-label">
                <span>Title</span>
                <span className="resume-editor__add-link-required">Required</span>
              </label>
              <input
                type="text"
                className="resume-editor__add-link-input"
                placeholder="e.g., My Portfolio"
                value={newLinkTitle}
                onChange={(e) => setNewLinkTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div className="resume-editor__add-link-field">
              <label className="resume-editor__add-link-label">
                <span>URL</span>
                <span className="resume-editor__add-link-required">Required</span>
              </label>
              <input
                type="url"
                className="resume-editor__add-link-input"
                placeholder="https://example.com"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newLinkTitle.trim() && newLinkUrl.trim()) {
                    handleSaveLink();
                  } else if (e.key === 'Escape') {
                    handleCancelLink();
                  }
                }}
              />
            </div>
            <div className="resume-editor__add-link-actions">
              <button
                className="resume-editor__add-link-cancel-btn"
                onClick={handleCancelLink}
              >
                Cancel
              </button>
              <button
                className="resume-editor__add-link-save-btn"
                onClick={handleSaveLink}
                disabled={!newLinkTitle.trim() || !newLinkUrl.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}
        
        {customLinks.length > 0 && (
          <div className="resume-editor__custom-links-list">
            {customLinks.map((link) => (
              <div key={link.uid} className="resume-editor__custom-link-item">
                <div className="resume-editor__custom-link-content">
                  <div className="resume-editor__custom-link-title">{link.title}</div>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resume-editor__custom-link-url"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {link.url}
                  </a>
                </div>
                <button
                  className="resume-editor__custom-link-delete"
                  onClick={() => {
                    const updatedCustomLinks = customLinks.filter(l => l.uid !== link.uid);
                    setCustomLinks(updatedCustomLinks);
                    
                    if (onLinkBlocksChange) {
                      const linkBlocks: LinkBlocks = {
                        uid: initialLinkBlocks?.uid,
                        display_format: localDisplayFormat,
                        linkedin_handler: links.find(l => l.type === 'linkedin')?.handle || null,
                        telegram_handler: links.find(l => l.type === 'telegram')?.handle || null,
                        whatsapp_handler: links.find(l => l.type === 'whatsapp')?.handle || null,
                        github_handler: links.find(l => l.type === 'github')?.handle || null,
                        twitter_handler: links.find(l => l.type === 'twitter')?.handle || null,
                        dribbble_handler: links.find(l => l.type === 'dribbble')?.handle || null,
                        custom_links: updatedCustomLinks.length > 0 ? updatedCustomLinks.map(l => ({ name: l.title, url: l.url })) : null,
                      };
                      onLinkBlocksChange(linkBlocks);
                    }
                  }}
                  title="Delete link"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

